import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import MenuList from '@components/MenuList';
import Tooltip from '@components/Tooltip';
import { useTranslation, useSurveysOptions, usePlaceholderResolver } from '@hooks';
import { isSurveySendable } from '@constants/surveys';
import { serializePlaceholdersToHtml } from '@helpers/text-editor';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';

const SurveysMenu = ({ disabled, setMessage, activeConversation, channelBrandId }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const placeholderResolver = usePlaceholderResolver(PLACEHOLDER_CONTEXT.INBOX);
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const companies = useSelector(state => state.account?.account?.companies || []);
    const { getBrandSurveysOptionsByIds, getBrandSurveysOptionsByBrandId } = useSurveysOptions();

    useEffect(() => {
        editor?.commands.clearContent(false);
    }, [activeConversation?.id]);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
        }
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const getLocationTokenValue = () => {
        if (activeConversation?.locations?.length === 1) {
            return companies.find(c => c.companyId === activeConversation.locations[0])?.locationName || '';
        }
        if (!activeConversation?.locations?.length && channelBrandId) {
            return companies.find(c => c.brand?.id === channelBrandId)?.brand?.title || '';
        }
        return '';
    };

    const onClickTemplate = survey => {
        const locationName = getLocationTokenValue();
        const newMessage = survey.message
            ?.replace(/\[name\]/g, activeConversation?.firstName || '')
            ?.replace(/\[last_name\]/g, activeConversation?.lastName || '')
            ?.replace(/\[location\]/g, locationName);
        const placeholderWithId = `[survey:${survey.id}]`;
        const messageForEditor = `${newMessage ? `${newMessage} ` : ''}${placeholderWithId}`;
        editor.commands.setContent(serializePlaceholdersToHtml(messageForEditor, placeholderResolver), {
            emitUpdate: false,
        });
        setMessage(JSON.stringify(editor?.getJSON() || {}));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const mapMenu = useMemo(() => {
        const surveys = activeConversation?.locations?.length
            ? getBrandSurveysOptionsByIds(activeConversation.locations)
            : getBrandSurveysOptionsByBrandId(channelBrandId);
        return surveys.filter(isSurveySendable).map(survey => ({
            value: survey.value,
            icon: null,
            text: survey.label,
            onClick: () => onClickTemplate({ id: survey.value, name: survey.label, message: survey.message }),
        }));
    }, [companies, activeConversation, channelBrandId]);

    return (
        <>
            <MenuList
                options={mapMenu}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('common.surveys')}
                menuButton={
                    <Tooltip title={t('common.surveys')} placement="top">
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={handleOpenMain}
                            disabled={disabled}
                            aria-label={t('common.surveys')}
                            sx={{
                                height: 18,
                                width: 18,
                                padding: '2px !important',
                                '& svg': { color: 'var(--text-secondary)', fontSize: 16 },
                            }}
                        >
                            <AssignmentRoundedIcon />
                        </IconButton>
                    </Tooltip>
                }
            />
        </>
    );
};

export default SurveysMenu;
